import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { contractAutoRoutingService } from '../../../services/contractAutoRoutingService';

const ApprovalPanel = ({ contract, userRole, onApprove, onReject, onRequestChanges }) => {
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEscalating, setIsEscalating] = useState(false);

  if (!contract) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <Icon name="CheckCircle" size={48} color="var(--color-muted-foreground)" />
        <p className="text-sm text-muted-foreground mt-4">
          اختر عقداً لعرض خيارات الموافقة
        </p>
      </div>
    );
  }

  const canApprove = () => {
    if (userRole === 'admin') return true;
    if (userRole === 'contract_specialist' && contract?.currentStage === 'legal_review') return true;
    return false;
  };

  const handleApprove = async () => {
    if (!comment?.trim()) {
      alert('الرجاء إضافة تعليق قبل الموافقة');
      return;
    }
    setIsSubmitting(true);
    await onApprove(contract?.id, comment);
    setComment('');
    setIsSubmitting(false);
  };

  const handleReject = async () => {
    if (!comment?.trim()) {
      alert('الرجاء إضافة سبب الرفض');
      return;
    }
    setIsSubmitting(true);
    await onReject(contract?.id, comment);
    setComment('');
    setIsSubmitting(false);
  };

  const handleRequestChanges = async () => {
    if (!comment?.trim()) {
      alert('الرجاء تحديد التعديلات المطلوبة');
      return;
    }
    setIsSubmitting(true);
    await onRequestChanges(contract?.id, comment);
    setComment('');
    setIsSubmitting(false);
  };

  const handleEscalate = async () => {
    if (!confirm('هل أنت متأكد من تصعيد هذا العقد إلى المدير؟')) return;

    setIsEscalating(true);
    try {
      const result = await contractAutoRoutingService?.escalateContract(
        contract?.id,
        'manual_escalation'
      );
      
      if (result?.success) {
        alert(`تم تصعيد العقد بنجاح إلى: ${result?.escalatedTo}`);
      }
    } catch (error) {
      console.error('Error escalating contract:', error);
      alert(error?.message || 'فشل تصعيد العقد');
    } finally {
      setIsEscalating(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 md:p-6 border-b border-border">
        <h3 className="text-lg md:text-xl font-heading font-bold text-foreground mb-2">
          إجراءات الموافقة
        </h3>
        <p className="text-xs md:text-sm text-muted-foreground">
          المرحلة الحالية: {contract?.currentStageLabel}
        </p>
      </div>
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="space-y-6">
          <div>
            <h4 className="text-sm md:text-base font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="GitBranch" size={18} />
              مسار الموافقة
            </h4>
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="divide-y divide-border">
                {contract?.approvalStages?.map((stage, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-3 p-4 transition-all ${
                      stage?.status === 'completed'
                        ? 'bg-success/5 hover:bg-success/10'
                        : stage?.status === 'current' ?'bg-accent/5 hover:bg-accent/10' :'hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {stage?.status === 'completed' ? (
                        <div className="w-8 h-8 rounded-full bg-success/20 flex items-center justify-center">
                          <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                        </div>
                      ) : stage?.status === 'current' ? (
                        <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center animate-pulse">
                          <Icon name="Clock" size={20} color="var(--color-accent)" />
                        </div>
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                          <Icon name="Circle" size={20} color="var(--color-muted-foreground)" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground">{stage?.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">{stage?.approver}</p>
                      {stage?.completedDate && (
                        <div className="flex items-center gap-1 mt-2">
                          <Icon name="Calendar" size={12} color="var(--color-muted-foreground)" />
                          <p className="text-xs text-muted-foreground font-data">
                            {stage?.completedDate}
                          </p>
                        </div>
                      )}
                    </div>
                    {stage?.status === 'completed' && (
                      <div className="flex-shrink-0">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/20 text-success">
                          مكتمل
                        </span>
                      </div>
                    )}
                    {stage?.status === 'current' && (
                      <div className="flex-shrink-0">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent/20 text-accent">
                          جاري
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm md:text-base font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="MessageSquare" size={18} />
              سجل التعليقات
            </h4>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {contract?.comments?.map((commentItem, index) => (
                <div key={index} className="bg-card border border-border rounded-lg p-3">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <p className="text-sm font-medium text-foreground">{commentItem?.author}</p>
                    <p className="text-xs text-muted-foreground font-data whitespace-nowrap">
                      {commentItem?.date}
                    </p>
                  </div>
                  <p className="text-sm text-foreground leading-relaxed">{commentItem?.text}</p>
                  {commentItem?.action && (
                    <div className="mt-2 pt-2 border-t border-border">
                      <span className="text-xs font-medium text-primary">{commentItem?.action}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {canApprove() && (
            <div>
              <h4 className="text-sm md:text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Edit3" size={18} />
                إضافة تعليق
              </h4>
              <Input
                type="text"
                placeholder="اكتب تعليقك هنا..."
                value={comment}
                onChange={(e) => setComment(e?.target?.value)}
                className="mb-4"
              />
            </div>
          )}
        </div>
      </div>
      {canApprove() && (
        <div className="p-4 md:p-6 border-t border-border bg-muted space-y-3">
          <div className="flex gap-2">
            <Button
              variant="default"
              onClick={handleApprove}
              disabled={!canApprove() || isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? (
                <Icon name="Loader" size={16} className="animate-spin" />
              ) : (
                <Icon name="CheckCircle" size={16} />
              )}
              <span className="mr-2">موافقة</span>
            </Button>
            <Button
              variant="outline"
              onClick={handleRequestChanges}
              disabled={isSubmitting}
              className="flex-1"
            >
              <Icon name="Edit" size={16} />
              <span className="mr-2">طلب تعديل</span>
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={isSubmitting}
              className="flex-1"
            >
              <Icon name="XCircle" size={16} />
              <span className="mr-2">رفض</span>
            </Button>
          </div>

          {/* Escalation Button */}
          <div className="mt-4 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={handleEscalate}
              disabled={isEscalating}
              className="w-full border-amber-500 text-amber-600 hover:bg-amber-50"
            >
              {isEscalating ? (
                <Icon name="Loader" size={16} className="animate-spin" />
              ) : (
                <Icon name="AlertTriangle" size={16} />
              )}
              <span className="mr-2">تصعيد إلى المدير</span>
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-2">
              سيتم إرسال إشعار عاجل إلى المدير المسؤول
            </p>
          </div>
        </div>
      )}
      {!canApprove() && (
        <div className="p-4 md:p-6 border-t border-border bg-muted">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Icon name="Lock" size={18} />
            <p className="text-sm">
              ليس لديك صلاحية الموافقة على هذا العقد في المرحلة الحالية
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalPanel;